/**
 * API Client
 * Handles communication with the WordPress REST API endpoints
 */

interface FormSuccessResponse {
  success: true;
  html: string;
  post_id?: number;
}

type TaxQuery = {
  taxonomy: string;
  field: string;
  terms: string | number | (string | number)[];
  operator?: string;
};

interface QueryParams {
  s?: string;
  post_type?: string | string[];
  posts_per_page?: number;
  paged?: number;
  post_status?: string | string[];
  orderby?: string;
  order?: string;
  program_activity?: string;
  tax_query?: TaxQuery[];
  meta_query?: any[];
  lang?: string;
}

interface Options {
  cache: {
    enabled?: boolean;
    ttl?: number; // Time to live in milliseconds
    cacheName?: string; // Cache storage name for HTTP cache
  }
}

class API {
  private baseUrl: string;
  private endpoint: string = '/wp-json/uu/v1';
  private readonly defaultCacheName = 'uu-api-cache';
  private readonly defaultCacheTTL = 5 * 60 * 1000;
  private options: Options = {
    cache: {
      enabled: true,
      ttl: this.defaultCacheTTL, // 5 minutes by default
      cacheName: this.defaultCacheName
    }
  };

  constructor(options?: Options) {
    this.baseUrl = window.location.origin;
    this.options = { ...this.options, ...options };
  }

  /**
   * Get posts from the API
   * @param params Query parameters
   * @returns Promise with posts response
   */
  async getPosts(params: QueryParams): Promise<string> {
    return await this.get<string>('/getPosts', params);
  }

  async addApplication(params: Record<string, any>): Promise<FormSuccessResponse> {
    return await this.post<FormSuccessResponse>('/applications', params);
  }

  async addNewsletterSubscription(params: Record<string, any>): Promise<FormSuccessResponse> {
    return await this.post<FormSuccessResponse>('/newsletter-subscriptions', params);
  }

  private getDefaultHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'X-Origin-Page': window.location.href.replace(/\/page\/\d+\//, '/'),
      'Accept-Language': window.Site.pll_language,
    };
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (response.headers.has('content-type') && response.headers.get('content-type')?.includes('text/html')) {
      return await response.text() as T;
    }

    return await response.json();
  }

  private async fetchResponse(method: 'GET' | 'POST', url: string, body?: unknown): Promise<Response> {
    const response = await fetch(url, {
      method,
      headers: this.getDefaultHeaders(),
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    if (!response.ok) {
      let message = `HTTP error! status: ${response.status}`;

      try {
        const errorResponse = await this.parseResponse<{ message?: string }>(response.clone());

        if (errorResponse && typeof errorResponse === 'object' && typeof errorResponse.message === 'string') {
          message = errorResponse.message;
        }
      } catch {
        // Keep the default message when the error body cannot be parsed.
      }

      throw new Error(message);
    }

    return response;
  }

  private getCacheName(): string {
    return this.options.cache.cacheName || this.defaultCacheName;
  }

  private getCacheTTL(): number {
    return this.options.cache.ttl || this.defaultCacheTTL;
  }

  private isCacheValid(response: Response): boolean {
    const cachedDate = new Date(response.headers.get('date') || 0);
    const age = Date.now() - cachedDate.getTime();

    return age < this.getCacheTTL();
  }

  private async getCachedResponse(cacheKey: string): Promise<Response | null> {
    const cache = await caches.open(this.getCacheName());
    const cachedResponse = await cache.match(cacheKey);

    if (!cachedResponse) {
      return null;
    }

    if (this.isCacheValid(cachedResponse)) {
      return cachedResponse;
    }

    await cache.delete(cacheKey);
    return null;
  }

  private async putCachedResponse(cacheKey: string, response: Response): Promise<void> {
    const cache = await caches.open(this.getCacheName());
    await cache.put(cacheKey, response.clone());
  }

  private async createPostCacheKey(url: string, body: unknown): Promise<string> {
    const hash = await this.hashString(`${url}:${JSON.stringify(body)}`);
    return `${url}?hash=${hash}`;
  }

  /**
   * Perform GET request
   * @param endpoint API endpoint path
   * @param params Query parameters
   * @returns Promise with response data
   */
  private async get<T>(endpoint: string, params: Record<string, any> = {}): Promise<T> {
    const url = this.buildUrl(endpoint, params);

    // Use HTTP Cache API if enabled
    if (this.options.cache.enabled && 'caches' in window) {
      return await this.getWithHttpCache<T>(url.toString());
    }

    // No cache - regular fetch
    try {
      const response = await this.fetchResponse('GET', url.toString());
      return await this.parseResponse<T>(response);
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  /**
   * Perform POST request
   * @param endpoint API endpoint path
   * @param params Query parameters
   * @returns Promise with response data
   */
  async post<T>(endpoint: string, params: Record<string, any> = {}): Promise<T> {
    const url = this.buildUrl(endpoint);

    // Use HTTP Cache API if enabled
    if (this.options.cache.enabled && 'caches' in window) {
      return await this.postWithHttpCache<T>(url.toString(), params);
    }

    // No cache - regular fetch
    try {
      const response = await this.fetchResponse('POST', url.toString(), params);
      return await this.parseResponse<T>(response);
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  /**
   * Build URL with query parameters
   * @param endpoint API endpoint path
   * @param params Query parameters
   * @returns URL object
   */
  private buildUrl(endpoint: string, params: Record<string, any> = {}): URL {
    const url = new URL(this.endpoint + endpoint, this.baseUrl);

    // Add language from cookies
    const lang = this.getLanguageFromCookies();
    if (lang) {
      params.lang = lang;
    }

    // Add query parameters to URL
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // For arrays, serialize as JSON if they contain objects
          if (value.length > 0 && typeof value[0] === 'object') {
            url.searchParams.append(key, JSON.stringify(value));
          } else {
            // For simple arrays, append each value
            value.forEach((item) => {
              url.searchParams.append(`${key}[]`, String(item));
            });
          }
        } else if (typeof value === 'object') {
          // For objects, serialize as JSON
          url.searchParams.append(key, JSON.stringify(value));
        } else {
          // For simple values, append as string
          url.searchParams.append(key, String(value));
        }
      }
    });

    return url;
  }

  /**
   * Get language code from Polylang cookies
   * @returns Language code or null
   */
  private getLanguageFromCookies(): string | null {
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'pll_language') {
        return decodeURIComponent(value);
      }
    }

    return null;
  }

  /**
   * Set custom endpoint
   * @param endpoint Custom endpoint path
   */
  setEndpoint(endpoint: string): void {
    this.endpoint = endpoint;
  }

  /**
   * Set custom base URL
   * @param baseUrl Custom base URL
   */
  setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }

  /**
   * Create hash from string
   * @param str String to hash
   * @returns Hash string
   */
  private async hashString(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Perform GET request with HTTP Cache API
   * @param url Request URL
   * @returns Promise with response data
   */
  private async getWithHttpCache<T>(url: string): Promise<T> {
    try {
      // Try to get from cache first
      const cachedResponse = await this.getCachedResponse(url);

      if (cachedResponse) {
        console.log('Returning HTTP cached data for:', url);
        return await this.parseResponse<T>(cachedResponse);
      }

      // Fetch fresh data
      const response = await this.fetchResponse('GET', url);

      // Store in cache
      await this.putCachedResponse(url, response);
      return await this.parseResponse<T>(response);
    } catch (error) {
      console.error('HTTP Cache API error:', error);
      throw error;
    }
  }

  /**
   * Perform POST request with HTTP Cache API
   * @param url Request URL
   * @param body Request body
   * @returns Promise with response data
   */
  private async postWithHttpCache<T>(url: string, body: any): Promise<T> {
    try {
      // Create unique cache key from URL + body hash (Cache API only supports GET/HEAD)
      const cacheKey = await this.createPostCacheKey(url, body);

      // Try to get from cache first
      const cachedResponse = await this.getCachedResponse(cacheKey);

      if (cachedResponse) {
        console.log('Returning HTTP cached data for:', url, body);
        return await this.parseResponse<T>(cachedResponse);
      }

      // Fetch fresh data
      const response = await this.fetchResponse('POST', url, body);

      // Store in cache with the unique cache key
      await this.putCachedResponse(cacheKey, response);
      return await this.parseResponse<T>(response);
    } catch (error) {
      console.error('HTTP Cache API error:', error);
      throw error;
    }
  }

  /**
   * Clear all cache
   */
  async clearCache(): Promise<void> {
    if ('caches' in window) {
      await caches.delete(this.getCacheName());
    }
  }

  /**
   * Clear specific cache entry
   * @param endpoint Endpoint to clear cache for
   * @param params Optional query parameters
   */
  async clearCacheForUrl(endpoint: string, params: Record<string, any> = {}): Promise<void> {
    if ('caches' in window) {
      const url = this.buildUrl(endpoint, params);
      const cache = await caches.open(this.getCacheName());
      await cache.delete(url.toString());
    }
  }

  /**
   * Enable or disable cache
   * @param enabled Whether cache should be enabled
   */
  setCacheEnabled(enabled: boolean): void {
    this.options.cache.enabled = enabled;
  }

  /**
   * Set cache TTL
   * @param ttl Time to live in milliseconds
   */
  setCacheTTL(ttl: number): void {
    this.options.cache.ttl = ttl;
  }
}

export default API;
export type { QueryParams, Options };
